/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { AlertCircle, CheckCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import invoiceService from '../services/invoiceService';
import './WorkerInvoices.css';

const CreditNoteManager = ({ workerPaymentProfileId, onClose }) => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedTransfers, setSelectedTransfers] = useState([]);
  const [reason, setReason] = useState('DATA_ERROR');
  const [reasonNotes, setReasonNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Custom mode state
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  // FatturaPA description (always available)
  const [description, setDescription] = useState('');

  // Newly created credit note state
  const [createdCreditNote, setCreatedCreditNote] = useState(null);

  useEffect(() => {
    if (workerPaymentProfileId) {
      loadIssuedInvoices();
    }
  }, [workerPaymentProfileId]);

  const loadIssuedInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoiceService.getIssuedInvoices(workerPaymentProfileId);
      if (response.success) {
        setInvoices(response.invoices);
      } else {
        setError(response.message || 'Errore nel caricamento delle fatture');
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Errore nel caricamento delle fatture');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferToggle = (transferId) => {
    setSelectedTransfers(prev => {
      if (prev.includes(transferId)) {
        return prev.filter(id => id !== transferId);
      } else {
        return [...prev, transferId];
      }
    });
  };

  const handleSelectInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    // Generate default description
    const invoiceDate = new Date(invoice.generatedAt).toLocaleDateString('it-IT');
    const defaultDescription = `Nota di credito - Riferimento fattura: ${invoice.invoiceNumber} del ${invoiceDate}`;
    setDescription(defaultDescription);
  };

  const handleCreateCreditNote = async () => {
    if (!selectedInvoice) {
      setError('Seleziona una fattura');
      return;
    }

    // Validation based on mode
    if (isCustomMode) {
      if (!customAmount || parseFloat(customAmount) <= 0) {
        setError('Inserisci un importo valido');
        return;
      }
      if (!description || description.trim() === '') {
        setError('Inserisci una descrizione');
        return;
      }
    } else {
      if (selectedTransfers.length === 0) {
        setError('Seleziona almeno un transfer');
        return;
      }
      if (reason === 'OTHER' && !reasonNotes) {
        setError('Inserisci una motivazione per "Altro"');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        reasonType: isCustomMode ? 'MANUAL_ADJUSTMENT' : reason,
        reasonNotes: reasonNotes || undefined,
        description: description || undefined  // Add custom FatturaPA description
      };

      if (isCustomMode) {
        payload.customAmount = Math.round(parseFloat(customAmount) * 100); // Convert to cents
        // Note: customDescription removed, using global description field
      } else {
        payload.transferIds = selectedTransfers;
      }

      const response = await invoiceService.createCreditNote(selectedInvoice.id, payload);

      if (response.success) {
        setCreatedCreditNote(response.data.creditNote);
        setSuccess(`Nota di credito ${response.data.creditNote.creditNoteNumber} creata con successo (BOZZA)`);
        // Reload invoices to show updated credit notes
        await loadIssuedInvoices();
        // Reset selection
        setSelectedInvoice(null);
        setSelectedTransfers([]);
        setReasonNotes('');
        setCustomAmount('');
        setDescription('');
        setIsCustomMode(false);
      } else {
        setError(response.message || 'Errore nella creazione della nota di credito');
      }
    } catch (err) {
      console.error('Error creating credit note:', err);
      setError('Errore nella creazione della nota di credito');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCreditNote = async () => {
    if (!createdCreditNote) return;

    if (!window.confirm(`Vuoi inviare la nota di credito ${createdCreditNote.creditNoteNumber} ad Acube?\n\nLa nota verrà processata e non potrà più essere modificata.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await invoiceService.sendCreditNoteToAcube(createdCreditNote.id);
      setSuccess(`Nota di credito ${createdCreditNote.creditNoteNumber} inviata ad Acube con successo!`);
      setCreatedCreditNote(null);
      await loadIssuedInvoices();
    } catch (err) {
      console.error('Error sending credit note:', err);
      setError('Errore nell\'invio della nota di credito: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  const reasonOptions = [
    { value: 'CHARGEBACK', label: 'Chargeback' },
    { value: 'REFUND_REQUESTED', label: 'Rimborso Richiesto' },
    { value: 'DATA_ERROR', label: 'Errore nei Dati' },
    { value: 'DUPLICATE', label: 'Duplicato' },
    { value: 'WORKER_DISPUTE', label: 'Contestazione Worker' },
    { value: 'ADMIN_CORRECTION', label: 'Correzione Amministrativa' },
    { value: 'TRANSFER_REVERSAL', label: 'Transfer Reversato' },
    { value: 'MANUAL_ADJUSTMENT', label: 'Aggiustamento Manuale' },
    { value: 'OTHER', label: 'Altro' }
  ];

  const getAvailableTransfers = (invoice) => {
    // Show all transfers, but mark which ones are already associated with credit notes
    return invoice.tipTransfers || [];
  };

  const getTotalSelected = () => {
    if (!selectedInvoice) return 0;
    const transfers = selectedInvoice.tipTransfers.filter(t => selectedTransfers.includes(t.id));
    return transfers.reduce((sum, t) => sum + t.feeTotal, 0);
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="card">
        <p>⏳ Caricamento fatture emesse...</p>
      </div>
    );
  }

  return (
    <div className="credit-note-manager">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>📝 Gestione Note di Credito</h3>
          {onClose && (
            <button onClick={onClose} className="btn btn-secondary">Chiudi</button>
          )}
        </div>

        {error && (
          <div style={{ background: '#fee', border: '1px solid #fcc', borderRadius: '4px', padding: '12px', marginBottom: '15px', color: '#c00' }}>
            <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
            {error}
          </div>
        )}

        {success && (
          <div style={{ background: '#efe', border: '1px solid #cfc', borderRadius: '4px', padding: '12px', marginBottom: '15px', color: '#060' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <CheckCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
                {success}
              </div>
              {createdCreditNote && (
                <button
                  onClick={handleSendCreditNote}
                  className="btn btn-primary"
                  style={{
                    fontSize: '13px',
                    padding: '6px 12px',
                    background: '#1976d2',
                    marginLeft: '10px'
                  }}
                  disabled={loading}
                >
                  📤 Invia ad Acube
                </button>
              )}
            </div>
          </div>
        )}

        {!selectedInvoice ? (
          <>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Seleziona una fattura emessa per creare una nota di credito manuale.
            </p>

            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {invoices.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                  Nessuna fattura emessa trovata
                </p>
              ) : (
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>Numero Fattura</th>
                      <th>Periodo</th>
                      <th>Totale</th>
                      <th>Note Credito</th>
                      <th>Azione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(invoice => {
                      const availableTransfers = getAvailableTransfers(invoice);
                      const hasCreditNotes = invoice.creditNotes && invoice.creditNotes.length > 0;

                      return (
                        <tr key={invoice.id}>
                          <td style={{ fontWeight: '600' }}>{invoice.invoiceNumber}</td>
                          <td style={{ fontSize: '13px' }}>
                            {new Date(invoice.periodStart).toLocaleDateString('it-IT')} - {new Date(invoice.periodEnd).toLocaleDateString('it-IT')}
                          </td>
                          <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                            {formatCurrency(invoice.totalFeeTotal)}
                          </td>
                          <td>
                            {hasCreditNotes ? (
                              <span style={{ color: '#f57c00', fontSize: '13px' }}>
                                {invoice.creditNotes.length} emesse
                              </span>
                            ) : (
                              <span style={{ color: '#999', fontSize: '13px' }}>Nessuna</span>
                            )}
                          </td>
                          <td>
                            {availableTransfers.length > 0 ? (
                              <button
                                onClick={() => handleSelectInvoice(invoice)}
                                className="btn btn-primary"
                                style={{ fontSize: '13px', padding: '6px 12px' }}
                              >
                                Crea Nota di Credito
                              </button>
                            ) : (
                              <span style={{ color: '#999', fontSize: '12px' }}>
                                Tutti i transfer già coperti
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Invoice selected - show transfer selection */}
            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: '0 0 5px 0' }}>Fattura: {selectedInvoice.invoiceNumber}</h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                    Periodo: {new Date(selectedInvoice.periodStart).toLocaleDateString('it-IT')} - {new Date(selectedInvoice.periodEnd).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedInvoice(null);
                    setSelectedTransfers([]);
                    setError(null);
                  }}
                  className="btn btn-secondary"
                  style={{ fontSize: '13px' }}
                >
                  ← Cambia Fattura
                </button>
              </div>
            </div>

            {/* Description Field */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                📄 Descrizione Nota di Credito (per FatturaPA)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrizione che apparirà nella nota di credito..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  fontSize: '14px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '6px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Questa descrizione apparirà nella nota di credito inviata ad Acube. Se lasciata vuota, verrà generata automaticamente.
              </div>
            </div>

            {/* Mode Toggle */}
            <div style={{
              background: '#f0f4ff',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '2px solid #d0e0ff'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: '600' }}>
                <input
                  type="checkbox"
                  checked={isCustomMode}
                  onChange={(e) => {
                    setIsCustomMode(e.target.checked);
                    setSelectedTransfers([]);
                    setError(null);
                  }}
                  style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ color: '#0066cc' }}>
                  💰 Modalità Importo Personalizzato
                </span>
              </label>
              <p style={{ margin: '8px 0 0 28px', fontSize: '13px', color: '#666' }}>
                Attiva questa opzione per creare una nota di credito con importo e descrizione manuali, senza collegare trasferimenti specifici.
              </p>
            </div>

            {isCustomMode ? (
              /* Custom Mode: Manual amount only */
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
                    💰 Importo (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Es: 50.00"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '2px solid #ddd',
                      fontSize: '16px',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </>
            ) : (
              /* Standard Mode: Transfer selection */
              <>
                {/* Reason selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
                    Motivo della Nota di Credito
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    {reasonOptions.filter(opt => opt.value !== 'MANUAL_ADJUSTMENT').map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {reason === 'OTHER' && (
                    <textarea
                      value={reasonNotes}
                      onChange={(e) => setReasonNotes(e.target.value)}
                      placeholder="Descrivi il motivo..."
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        marginTop: '10px',
                        minHeight: '80px',
                        fontFamily: 'inherit'
                      }}
                    />
                  )}
                </div>

                {/* Transfer selection */}
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ marginBottom: '10px' }}>Seleziona Transfer da Includere nella Nota di Credito</h5>

                  {getAvailableTransfers(selectedInvoice).length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                      Tutti i transfer di questa fattura hanno già una nota di credito
                    </p>
                  ) : (
                    <div style={{
                      background: 'white',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '13px'
                      }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                            <th style={{
                              width: '40px',
                              padding: '14px 12px',
                              textAlign: 'center',
                              fontWeight: '600',
                              color: '#495057'
                            }}>
                              <input
                                type="checkbox"
                                style={{ cursor: 'pointer' }}
                                checked={(() => {
                                  const availableTransfers = getAvailableTransfers(selectedInvoice).filter(t => !t.creditNoteId);
                                  return availableTransfers.length > 0 && selectedTransfers.length === availableTransfers.length;
                                })()}
                                onChange={(e) => {
                                  const availableTransfers = getAvailableTransfers(selectedInvoice).filter(t => !t.creditNoteId);
                                  if (e.target.checked) {
                                    setSelectedTransfers(availableTransfers.map(t => t.id));
                                  } else {
                                    setSelectedTransfers([]);
                                  }
                                }}
                              />
                            </th>
                            <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>
                              ID Transfer
                            </th>
                            <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>
                              Data
                            </th>
                            <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>
                              Venue
                            </th>
                            <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>
                              Importo
                            </th>
                            <th style={{ padding: '14px 12px', textAlign: 'right', fontWeight: '600', color: '#495057' }}>
                              Commissioni
                            </th>
                            <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '600', color: '#495057' }}>
                              Stato
                            </th>
                            <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '600', color: '#495057' }}>
                              Nota di Credito
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAvailableTransfers(selectedInvoice).map((transfer, idx) => {
                            const isAlreadyUsed = !!transfer.creditNoteId;
                            return (
                              <tr
                                key={transfer.id}
                                style={{
                                  background: isAlreadyUsed ? '#f8f8f8' : (idx % 2 === 0 ? 'white' : '#fafafa'),
                                  borderBottom: '1px solid #f0f0f0',
                                  transition: 'background 0.15s',
                                  opacity: isAlreadyUsed ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = isAlreadyUsed ? '#f0f0f0' : '#e3f2fd'}
                                onMouseLeave={(e) => e.currentTarget.style.background = isAlreadyUsed ? '#f8f8f8' : (idx % 2 === 0 ? 'white' : '#fafafa')}
                              >
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    style={{ cursor: isAlreadyUsed ? 'not-allowed' : 'pointer' }}
                                    checked={selectedTransfers.includes(transfer.id)}
                                    disabled={isAlreadyUsed}
                                    onChange={() => !isAlreadyUsed && handleTransferToggle(transfer.id)}
                                    title={isAlreadyUsed ? 'Transfer già associato a una nota di credito' : 'Seleziona questo transfer'}
                                  />
                                </td>
                                <td style={{
                                  padding: '12px',
                                  fontFamily: 'monospace',
                                  fontSize: '11px',
                                  color: '#666',
                                  maxWidth: '180px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {transfer.id}
                                </td>
                                <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                                  {new Date(transfer.createdAt).toLocaleDateString('it-IT')}
                                </td>
                                <td style={{ padding: '12px', fontWeight: '500' }}>
                                  {transfer.tipPaymentIntent?.venue?.name || 'N/A'}
                                </td>
                                <td style={{
                                  padding: '12px',
                                  textAlign: 'right',
                                  fontFamily: 'monospace',
                                  color: '#2c3e50'
                                }}>
                                  {formatCurrency(transfer.amount)}
                                </td>
                                <td style={{
                                  padding: '12px',
                                  textAlign: 'right',
                                  fontFamily: 'monospace',
                                  fontWeight: '600',
                                  color: '#28a745'
                                }}>
                                  {formatCurrency(transfer.feeTotal)}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  {transfer.reversed ? (
                                    <span style={{
                                      background: '#ffebee',
                                      color: '#c62828',
                                      padding: '4px 10px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      display: 'inline-block',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      🔴 REVERSED
                                    </span>
                                  ) : (
                                    <span style={{
                                      color: '#4caf50',
                                      fontSize: '14px',
                                      fontWeight: '600'
                                    }}>
                                      ✓
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  {transfer.creditNoteId ? (
                                    <span style={{
                                      background: '#fff3e0',
                                      color: '#f57c00',
                                      padding: '4px 8px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      display: 'inline-block'
                                    }}>
                                      📝 {transfer.creditNote?.creditNoteNumber || 'NC-XXX'}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#999', fontSize: '12px' }}>
                                      -
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Summary */}
            {((!isCustomMode && selectedTransfers.length > 0) || (isCustomMode && customAmount)) && (
              <div style={{
                background: '#e3f2fd',
                border: '1px solid #2196f3',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: '0 0 5px 0', fontWeight: '600', color: '#1976d2' }}>
                      Riepilogo Nota di Credito
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>
                      {isCustomMode
                        ? 'Importo personalizzato'
                        : `${selectedTransfers.length} transfer selezionati`
                      }
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>Totale da Rimborsare</p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1976d2', fontFamily: 'monospace' }}>
                      {isCustomMode
                        ? `€ ${parseFloat(customAmount || 0).toFixed(2)}`
                        : formatCurrency(getTotalSelected())
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setSelectedInvoice(null);
                  setSelectedTransfers([]);
                  setCustomAmount('');
                  setDescription('');
                  setIsCustomMode(false);
                  setError(null);
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Annulla
              </button>
              <button
                onClick={handleCreateCreditNote}
                className="btn btn-primary"
                disabled={
                  loading ||
                  (!isCustomMode && selectedTransfers.length === 0) ||
                  (isCustomMode && (!customAmount || !description))
                }
              >
                {loading ? '⏳ Creazione...' : '✓ Crea Nota di Credito (BOZZA)'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreditNoteManager;
